import { THEME_A_CSS_VARIABLES } from "../../../design-system/v2/foundations/tokens";
import { THEME_VARIANTS } from "../../../design-system/v2/foundations/themes";

/**
 * Thème PDF unique de l'application, dérivé des thèmes du design system V2
 * (`foundations/tokens` + `foundations/themes`). Toute génération PDF — jsPDF
 * côté web, pdfkit côté fonctions (miroir `functions/lib/pdfTheme.js`) — lit
 * ses couleurs ici, et les lit VIVANTES : `PDF_HEX.brand`, `PDF_RGB.brand`
 * (triplet itérable) et `PDF_THEME.table` se résolvent à l'accès contre le
 * réglage d'impression actif du parc (`configurePdfTheme`) — palette choisie
 * parmi `PRINT_THEME_IDS` et mode économie d'encre (gris, sans aplats).
 *
 * `pdfTheme.test.js` garantit que les palettes restent celles des jetons CSS,
 * que le miroir des fonctions est identique et que le mode économie d'encre
 * n'imprime que des gris ; `pdfThemeGuard.test.js` qu'aucun générateur ne
 * conserve une palette propre.
 *
 * Polices : jsPDF n'embarque que ses polices standard ; Helvetica joue le rôle
 * d'Inter (corps) et de Foco (titres). Les tailles sont en points.
 */

// Correspondance rôle du thème PDF → jeton CSS (source de vérité).
export const PDF_TOKEN_SOURCES = Object.freeze({
  text: "--ak-v2-text",
  muted: "--ak-v2-text-muted",
  surface: "--ak-v2-surface",
  subtle: "--ak-v2-surface-subtle",
  canvas: "--ak-v2-surface-canvas-tint",
  border: "--ak-v2-border",
  borderStrong: "--ak-v2-border-strong",
  brand: "--ak-v2-brand",
  brandHover: "--ak-v2-brand-hover",
  brandSoft: "--ak-v2-brand-soft",
  brandInk: "--ak-v2-brand-ink",
  highlight: "--ak-v2-highlight",
  highlightHover: "--ak-v2-highlight-hover",
  highlightSoft: "--ak-v2-highlight-soft",
  highlightInk: "--ak-v2-highlight-ink",
  critical: "--ak-v2-critical",
  criticalSoft: "--ak-v2-critical-soft",
  information: "--ak-v2-signal-information",
  progress: "--ak-v2-signal-progress",
  warning: "--ak-v2-signal-warning",
  inverse: "--ak-v2-surface-inverse",
  inverseRaised: "--ak-v2-surface-inverse-raised",
  collectionObservation: "--ak-v2-collection-observation",
  collectionQuestionnaire: "--ak-v2-collection-questionnaire",
  collectionScan: "--ak-v2-collection-scan",
  collectionObservationSoft: "--ak-v2-collection-observation-soft",
  collectionQuestionnaireSoft: "--ak-v2-collection-questionnaire-soft",
  collectionScanSoft: "--ak-v2-collection-scan-soft",
  datavizCorail: "--ak-v2-dataviz-corail",
  datavizAmbre: "--ak-v2-dataviz-ambre",
  datavizTurquoise: "--ak-v2-dataviz-turquoise",
  datavizBleu: "--ak-v2-dataviz-bleu",
  datavizViolet: "--ak-v2-dataviz-violet",
  datavizRose: "--ak-v2-dataviz-rose",
  ambianceTerracotta: "--ak-v2-ambiance-terracotta",
  ambianceSable: "--ak-v2-ambiance-sable",
  ambianceOlive: "--ak-v2-ambiance-olive",
  ambianceSauge: "--ak-v2-ambiance-sauge",
  ambianceArdoise: "--ak-v2-ambiance-ardoise",
  ambiancePrune: "--ak-v2-ambiance-prune",
});

export const PDF_ROLES = Object.freeze(Object.keys(PDF_TOKEN_SOURCES));

/**
 * Palettes d'impression proposées au parc : le thème « AKONGO » (défaut),
 * tiré de la charte du site akongo.io, puis quatre déclinaisons ÉDITORIALES
 * construites sur le même modèle (forêt, océan, ardoise, savane) : encre
 * presque noire teintée, papier et sable légers, un seul accent profond et un
 * surligneur doux. Les thèmes sombres ou d'accessibilité de l'interface ne
 * sont pas des thèmes d'impression.
 */
export const PRINT_THEME_IDS = Object.freeze(["akongo", "foret", "ocean", "ardoise", "savane"]);
export const DEFAULT_PRINT_THEME = "akongo";

/**
 * Charte du site akongo.io (jetons `--sig-*` de la feuille de style publique,
 * relevés le 28/09/2026) : encre presque noire, papier chaud, sable, soleil,
 * olive. Source du thème d'impression « AKONGO ».
 */
export const AKONGO_SIGNATURE_TOKENS = Object.freeze({
  ink: "#12120f",
  ink2: "#2c2b27",
  mute: "#6d665b",
  taupe: "#8a7968",
  field: "#d6cfc2",
  sand: "#f4f0e8",
  cream: "#fbf6df",
  paper: "#fffefb",
  sun: "#f3db4c",
  olive: "#5d8a2b",
  oliveDark: "#44691c",
  oliveDeep: "#3c5d1b",
  leaf: "#e5edd6",
});

const normalizeHex = (value) => String(value || "").trim().toLowerCase();

const paletteFromVariant = (variantId) => {
  const overrides = (THEME_VARIANTS[variantId] || THEME_VARIANTS.foret).overrides || {};
  return Object.freeze(Object.fromEntries(
    Object.entries(PDF_TOKEN_SOURCES).map(([role, token]) => [role, normalizeHex(overrides[token] || THEME_A_CSS_VARIABLES[token])]),
  ));
};

/**
 * Économie d'encre : encres noires et grises, AUCUN aplat (fonds doux blancs),
 * filets gris, séries de graphiques en rampe de gris — lisible en noir et blanc
 * et sur une imprimante laser économe.
 */
export const INK_SAVING_PALETTE = Object.freeze({
  text: "#111111",
  muted: "#555555",
  surface: "#ffffff",
  subtle: "#ffffff",
  canvas: "#f2f2f2",
  border: "#b3b3b3",
  borderStrong: "#7a7a7a",
  brand: "#1f1f1f",
  brandHover: "#000000",
  brandSoft: "#ffffff",
  brandInk: "#ffffff",
  highlight: "#4d4d4d",
  highlightHover: "#333333",
  highlightSoft: "#ffffff",
  highlightInk: "#111111",
  critical: "#2b2b2b",
  criticalSoft: "#ffffff",
  information: "#3d3d3d",
  progress: "#8c8c8c",
  warning: "#333333",
  inverse: "#1f1f1f",
  inverseRaised: "#333333",
  collectionObservation: "#2b2b2b",
  collectionQuestionnaire: "#4d4d4d",
  collectionScan: "#707070",
  collectionObservationSoft: "#ffffff",
  collectionQuestionnaireSoft: "#ffffff",
  collectionScanSoft: "#ffffff",
  datavizCorail: "#1a1a1a",
  datavizAmbre: "#4d4d4d",
  datavizTurquoise: "#808080",
  datavizBleu: "#333333",
  datavizViolet: "#666666",
  datavizRose: "#999999",
  ambianceTerracotta: "#262626",
  ambianceSable: "#595959",
  ambianceOlive: "#8c8c8c",
  ambianceSauge: "#a6a6a6",
  ambianceArdoise: "#404040",
  ambiancePrune: "#737373",
});

/**
 * Jetons éditoriaux d'un thème d'impression (même modèle pour les cinq) :
 * encre, encre secondaire, gris de texte, sable (fonds doux), papier, filets,
 * accent (titres de section, séries principales, succès) et sa version
 * profonde, fond d'accent, surligneur (repères, soulignés) et son fond doux.
 * L'accent n'est jamais un aplat d'en-tête : le document reste d'encre et de
 * papier (retour équipe du 28/09/2026).
 */
export const PRINT_THEME_TOKENS = Object.freeze({
  akongo: Object.freeze({
    ink: AKONGO_SIGNATURE_TOKENS.ink, ink2: AKONGO_SIGNATURE_TOKENS.ink2, mute: AKONGO_SIGNATURE_TOKENS.mute,
    sand: AKONGO_SIGNATURE_TOKENS.sand, paper: AKONGO_SIGNATURE_TOKENS.paper, border: "#e6e0d4", field: AKONGO_SIGNATURE_TOKENS.field,
    accent: AKONGO_SIGNATURE_TOKENS.oliveDark, accentDeep: AKONGO_SIGNATURE_TOKENS.oliveDeep, accentSoft: AKONGO_SIGNATURE_TOKENS.leaf,
    marker: AKONGO_SIGNATURE_TOKENS.sun, markerHover: "#e0c53a", markerSoft: AKONGO_SIGNATURE_TOKENS.cream,
  }),
  foret: Object.freeze({
    ink: "#13261e", ink2: "#22382f", mute: "#55665e", sand: "#eef2ee", paper: "#fbfcfa", border: "#dde5df", field: "#c2cfc7",
    accent: "#2e6b4e", accentDeep: "#235640", accentSoft: "#e2ede6", marker: "#e7c35a", markerHover: "#d6b045", markerSoft: "#fbf4df",
  }),
  ocean: Object.freeze({
    ink: "#0f252b", ink2: "#1f363c", mute: "#50666c", sand: "#ecf2f3", paper: "#fbfdfd", border: "#d9e4e6", field: "#bccdd1",
    accent: "#1d6670", accentDeep: "#15515a", accentSoft: "#deedef", marker: "#f0b98a", markerHover: "#e3a472", markerSoft: "#fdf0e4",
  }),
  ardoise: Object.freeze({
    ink: "#151a22", ink2: "#262d38", mute: "#5a6371", sand: "#eff1f4", paper: "#fcfcfd", border: "#dfe3e9", field: "#c3cad4",
    accent: "#3d5877", accentDeep: "#2f465f", accentSoft: "#e3e9f1", marker: "#dcb65c", markerHover: "#cba447", markerSoft: "#f8f1dd",
  }),
  savane: Object.freeze({
    ink: "#211910", ink2: "#33291d", mute: "#6a5d4d", sand: "#f4eee3", paper: "#fffdf9", border: "#e6ddce", field: "#d1c4ae",
    accent: "#85572a", accentDeep: "#6f4721", accentSoft: "#f5ece0", marker: "#eab04a", markerHover: "#d99c34", markerSoft: "#fcf1d9",
  }),
});

/**
 * Palette complète d'un thème : les jetons éditoriaux sur la base du design
 * system (signaux, collections et séries de graphiques de la forêt, lisibles
 * sur tous les papiers).
 */
const editorialPalette = (tokens) => Object.freeze({
  ...paletteFromVariant("foret"),
  text: tokens.ink,
  muted: tokens.mute,
  surface: "#ffffff",
  subtle: tokens.sand,
  canvas: tokens.paper,
  border: tokens.border,
  borderStrong: tokens.field,
  brand: tokens.accent,
  brandHover: tokens.accentDeep,
  brandSoft: tokens.accentSoft,
  brandInk: "#ffffff",
  highlight: tokens.marker,
  highlightHover: tokens.markerHover,
  highlightSoft: tokens.markerSoft,
  highlightInk: tokens.ink,
  inverse: tokens.ink,
  inverseRaised: tokens.ink2,
});

const PALETTES = Object.freeze(Object.fromEntries(PRINT_THEME_IDS.map((id) => [id, editorialPalette(PRINT_THEME_TOKENS[id])])));

/** Réglage d'impression normalisé : `{ themeId, inkSaving }` (document du parc `printTheme`). */
export const normalizePrintTheme = (value = {}) => Object.freeze({
  inkSaving: value?.inkSaving === true,
  themeId: PRINT_THEME_IDS.includes(value?.themeId) ? value.themeId : DEFAULT_PRINT_THEME,
});

export const resolvePdfPalette = (settings = {}) => {
  const { inkSaving, themeId } = normalizePrintTheme(settings);
  return inkSaving ? INK_SAVING_PALETTE : PALETTES[themeId];
};

/** Métadonnées des palettes pour l'interface de réglage (nuanciers : encre, sable, accent, surligneur). */
export const PRINT_THEMES = Object.freeze(PRINT_THEME_IDS.map((id) => Object.freeze({
  id,
  swatches: Object.freeze({ brand: PALETTES[id].brand, brandSoft: PALETTES[id].brandSoft, highlight: PALETTES[id].highlight, subtle: PALETTES[id].subtle, text: PALETTES[id].text }),
})));

// Réglage ACTIF (celui du parc, posé au chargement de la session ou par un export).
let active = normalizePrintTheme();
export const configurePdfTheme = (settings = {}) => { active = normalizePrintTheme(settings); return active; };
export const getPdfThemeSettings = () => active;
export const isInkSaving = () => active.inkSaving;
const activePalette = () => resolvePdfPalette(active);

export const hexToRgb = (hex) => {
  const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(String(hex || "").trim());
  if (!match) throw new Error(`pdfTheme: invalid hex colour "${hex}"`);
  return [parseInt(match[1], 16), parseInt(match[2], 16), parseInt(match[3], 16)];
};

/** Mélange une couleur vers le blanc : `ratio` = part de la couleur conservée (0 → blanc, 1 → couleur). */
export const tintRgb = (color, ratio) => {
  const rgb = Array.isArray(color) || (color && typeof color === "object") ? [color[0], color[1], color[2]] : hexToRgb(color);
  return rgb.map((channel) => Math.round(255 - (255 - channel) * ratio));
};

// Hex vivants : `PDF_HEX.brand` lit la palette active à chaque accès.
// Cible NON figée : l'invariant des Proxy interdit de renvoyer une autre valeur
// qu'une propriété gelée. Les rôles restent les clés énumérables.
export const PDF_HEX = new Proxy({ ...PALETTES[DEFAULT_PRINT_THEME] }, {
  get: (base, role) => (typeof role === "string" && role in base ? activePalette()[role] : Reflect.get(base, role)),
});

// Triplets vivants : `PDF_RGB.brand` est un tableau [r, g, b] dont les valeurs
// se résolvent à la lecture (spread, index, map, autotable), même capturé
// dans une constante de module par un générateur.
const liveRgb = (role) => new Proxy([0, 0, 0], {
  get: (target, property) => {
    const rgb = hexToRgb(activePalette()[role]);
    if (property === "length") return 3;
    if (property === "0" || property === "1" || property === "2") return rgb[Number(property)];
    if (property === Symbol.iterator) return function* iterate() { yield* rgb; };
    if (property === "toJSON") return () => rgb;
    return Reflect.get(target, property);
  },
});
const RGB_PROXIES = Object.freeze(Object.fromEntries(PDF_ROLES.map((role) => [role, liveRgb(role)])));
export const PDF_RGB = Object.freeze(RGB_PROXIES);

/** Intentions sémantiques : couleur d'accent, fond doux et encre lisible sur ce fond. */
export const PDF_INTENTS = Object.freeze({
  neutral: Object.freeze({ accent: "muted", soft: "subtle", ink: "text" }),
  success: Object.freeze({ accent: "brand", soft: "brandSoft", ink: "brandHover" }),
  attention: Object.freeze({ accent: "highlight", soft: "highlightSoft", ink: "highlightInk" }),
  warning: Object.freeze({ accent: "warning", soft: "highlightSoft", ink: "warning" }),
  critical: Object.freeze({ accent: "critical", soft: "criticalSoft", ink: "critical" }),
  information: Object.freeze({ accent: "information", soft: "collectionQuestionnaireSoft", ink: "information" }),
  brand: Object.freeze({ accent: "brand", soft: "brandSoft", ink: "brandHover" }),
});

export const intentRoles = (intent) => PDF_INTENTS[intent] || PDF_INTENTS.neutral;
export const intentRgb = (intent, part = "accent") => PDF_RGB[intentRoles(intent)[part]];
export const intentHex = (intent, part = "accent") => PDF_HEX[intentRoles(intent)[part]];

/** Séries de graphiques (histogrammes, camemberts, courbes) dans l'ordre d'usage. */
export const PDF_CHART_SERIES = Object.freeze([
  "brand", "datavizBleu", "datavizAmbre", "datavizViolet", "datavizTurquoise", "datavizRose", "datavizCorail", "highlight", "ambianceArdoise", "ambianceSauge",
]);
/**
 * Économie d'encre : séries de graphiques en gris MOYENS alternés (clair /
 * foncé pour rester distinguables côte à côte), jamais en noir plein — une
 * barre noire consomme autant qu'un aplat.
 */
export const INK_SAVING_CHART_RAMP = Object.freeze(["#737373", "#b3b3b3", "#4d4d4d", "#cccccc", "#8c8c8c", "#3b3b3b", "#a6a6a6", "#5e5e5e", "#bfbfbf", "#999999"]);
export const chartSeriesHex = (index) => (isInkSaving() ? INK_SAVING_CHART_RAMP[index % INK_SAVING_CHART_RAMP.length] : PDF_HEX[PDF_CHART_SERIES[index % PDF_CHART_SERIES.length]]);
export const chartSeriesRgb = (index) => (isInkSaving() ? hexToRgb(chartSeriesHex(index)) : PDF_RGB[PDF_CHART_SERIES[index % PDF_CHART_SERIES.length]]);

export const PDF_FONT = Object.freeze({ family: "helvetica" });

/** Tailles en points ; le corps reste >= 9 pt pour l'impression. */
export const PDF_SIZE = Object.freeze({
  title: 18,
  subtitle: 11,
  section: 12.5,
  subsection: 10.5,
  body: 9.5,
  small: 8,
  tiny: 7,
  metric: 15,
});

/** Géométrie A4 en millimètres : marges, gouttières, rayons de « cartes ». */
export const PDF_LAYOUT = Object.freeze({
  margin: 14,
  headerHeight: 12,
  footerHeight: 12,
  gutter: 4,
  paragraphGap: 3,
  sectionGap: 7,
  radius: 1.5,
  ruleWidth: 12,
  ruleThickness: 0.9,
});

/**
 * Styles jspdf-autotable communs, vivants et SOBRES : en-tête sur fond doux,
 * texte d'encre gras et filet appuyé ; lignes séparées par un simple filet
 * horizontal, zébrage à peine teinté. La couleur de marque n'est plus un aplat
 * d'en-tête : elle reste un accent (retour du 28/09/2026 : « vert très
 * soutenu, agressif à l'œil »). En économie d'encre, aucun fond.
 */
const ROW_RULE = Object.freeze({ bottom: 0.15, left: 0, right: 0, top: 0 });
const HEAD_RULE = Object.freeze({ bottom: 0.45, left: 0, right: 0, top: 0 });
export const PDF_TABLE_STYLES = Object.freeze({
  get styles() {
    return { cellPadding: { bottom: 2, left: 2.4, right: 2.4, top: 2 }, font: PDF_FONT.family, fontSize: PDF_SIZE.body, lineColor: PDF_RGB.border, lineWidth: ROW_RULE, textColor: PDF_RGB.text, valign: "middle" };
  },
  get headStyles() {
    return isInkSaving()
      ? { fillColor: PDF_RGB.surface, fontSize: PDF_SIZE.small, fontStyle: "bold", halign: "left", lineColor: PDF_RGB.borderStrong, lineWidth: HEAD_RULE, textColor: PDF_RGB.text }
      : { fillColor: PDF_RGB.subtle, fontSize: PDF_SIZE.small, fontStyle: "bold", halign: "left", lineColor: PDF_RGB.borderStrong, lineWidth: HEAD_RULE, textColor: PDF_RGB.text };
  },
  get bodyStyles() { return { fillColor: PDF_RGB.surface }; },
  get alternateRowStyles() { return { fillColor: isInkSaving() ? PDF_RGB.surface : tintRgb(PDF_RGB.subtle, 0.45) }; },
  get footStyles() {
    return isInkSaving()
      ? { fillColor: PDF_RGB.surface, fontStyle: "bold", lineColor: PDF_RGB.borderStrong, lineWidth: { bottom: 0, left: 0, right: 0, top: 0.45 }, textColor: PDF_RGB.text }
      : { fillColor: PDF_RGB.subtle, fontStyle: "bold", lineColor: PDF_RGB.borderStrong, lineWidth: { bottom: 0, left: 0, right: 0, top: 0.45 }, textColor: PDF_RGB.text };
  },
  theme: "grid",
});

export const PDF_THEME = Object.freeze({
  chartSeries: PDF_CHART_SERIES,
  font: PDF_FONT,
  hex: PDF_HEX,
  intents: PDF_INTENTS,
  layout: PDF_LAYOUT,
  rgb: PDF_RGB,
  size: PDF_SIZE,
  table: PDF_TABLE_STYLES,
  get settings() { return active; },
});

export default PDF_THEME;
